/**
 * Safe sessionStorage access. Absent during SSR, and beyond that Safari in private browsing, a
 * browser with site data blocked, and a browser at its quota all throw on access or on setItem.
 * These helpers degrade to "no storage" so callers never break over a convenience.
 */

const getStore = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** Parsed JSON at `key`, or undefined if absent, unreadable, or not valid JSON. */
export const readSessionJson = (key: string): unknown => {
  const store = getStore();

  if (!store) {
    return undefined;
  }

  try {
    const raw = store.getItem(key);

    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/** False when the write didn't land, so callers know not to record it as persisted. */
export const writeSessionJson = (key: string, value: unknown): boolean => {
  const store = getStore();

  if (!store) {
    return false;
  }

  try {
    store.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

/** Synchronous, unlike a mirroring effect, so callers can rely on storage being clear before they continue. */
export const removeSessionJson = (key: string): void => {
  const store = getStore();

  try {
    store?.removeItem(key);
  } catch {
    // Same "no storage" degradation as the other helpers.
  }
};
